import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { roleLabel } from "@/lib/roles";
import { MagnifyingGlass, FloppyDisk, Broom, Check, Minus, Lock } from "@phosphor-icons/react";

const STATES = ["inherit", "grant", "deny"];

function StateToggle({ state, onChange, disabled, base, testid }) {
  return (
    <div className="flex overflow-hidden rounded-md border border-slate-200" data-testid={testid}>
      {STATES.map((s) => {
        const active = state === s;
        const label = s === "inherit" ? (base ? "Role ✓" : "Role —") : s === "grant" ? "Grant" : "Deny";
        const cls = active
          ? s === "grant" ? "bg-emerald-600 text-white"
          : s === "deny" ? "bg-red-600 text-white"
          : "bg-slate-700 text-white"
          : "bg-white text-slate-400 hover:bg-slate-50";
        return (
          <button
            key={s} type="button" disabled={disabled}
            onClick={() => onChange(s)}
            className={`px-2 py-1 font-mono-alt text-[10px] uppercase tracking-wider transition-colors ${cls}`}
            data-testid={`${testid}-${s}`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default function UserOverridesTab() {
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [grants, setGrants] = useState(new Set());
  const [denies, setDenies] = useState(new Set());
  const [reason, setReason] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/permissions/users");
      setUsers(data.users || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    loadUsers();
    api.get("/permissions/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
  }, []);

  const openUser = async (id) => {
    setSelectedId(id);
    setDetail(null);
    try {
      const { data } = await api.get(`/permissions/users/${id}`);
      setDetail(data);
      setGrants(new Set(data.grants));
      setDenies(new Set(data.denies));
      setReason("");
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const stateOf = (action) => (grants.has(action) ? "grant" : denies.has(action) ? "deny" : "inherit");

  const setState = (action, s) => {
    const g = new Set(grants); const d = new Set(denies);
    g.delete(action); d.delete(action);
    if (s === "grant") g.add(action);
    if (s === "deny") d.add(action);
    setGrants(g); setDenies(d); setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/permissions/users/${selectedId}`, {
        grants: [...grants], denies: [...denies], reason,
      });
      setDetail(data);
      setGrants(new Set(data.grants)); setDenies(new Set(data.denies));
      setDirty(false); setReason("");
      toast.success("Overrides saved — user notified");
      loadUsers();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const clearAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/permissions/users/${selectedId}`);
      setDetail(data);
      setGrants(new Set()); setDenies(new Set());
      setDirty(false);
      toast.success("All overrides cleared");
      loadUsers();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.email || "").includes(q) || (u.name || "").toLowerCase().includes(q));
  }, [users, search]);

  const baseSet = useMemo(() => new Set(detail?.role_base || []), [detail]);

  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-[320px_1fr]" data-testid="user-overrides-tab">
      {/* User list */}
      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 p-3">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search users…" className="h-8 pl-8 text-sm"
              data-testid="override-user-search"
            />
          </div>
        </div>
        <div className="max-h-[65vh] overflow-y-auto">
          {filtered.map((u) => (
            <button
              key={u.id} onClick={() => openUser(u.id)}
              className={`flex w-full items-center gap-2 border-b border-slate-50 px-3 py-2.5 text-left hover:bg-slate-50 ${selectedId === u.id ? "bg-blue-50/60" : ""}`}
              data-testid={`override-user-${u.email}`}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-slate-800">{u.name || u.email}</div>
                <div className="truncate font-mono-alt text-[10px] text-slate-400">{u.email} · {roleLabel(u.role)}</div>
              </div>
              {u.grants_count > 0 && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">+{u.grants_count}</Badge>}
              {u.denies_count > 0 && <Badge className="bg-red-100 text-red-700 hover:bg-red-100">−{u.denies_count}</Badge>}
            </button>
          ))}
          {filtered.length === 0 && <div className="p-6 text-center text-xs text-slate-400">No users match</div>}
        </div>
      </div>

      {/* Detail */}
      <div className="rounded-xl border border-slate-200 bg-white" data-testid="override-detail-panel">
        {!detail ? (
          <div className="flex h-full min-h-[300px] items-center justify-center p-8 text-sm text-slate-400" data-testid="override-empty-state">
            {selectedId ? "Loading…" : "Select a user to view and edit their permissions"}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="font-display text-sm font-bold text-slate-900" data-testid="override-detail-name">
                  {detail.user.name || detail.user.email}
                </div>
                <div className="font-mono-alt text-[10px] text-slate-400">
                  {detail.user.email} · {roleLabel(detail.user.role)} · {detail.effective.length} effective permission{detail.effective.length === 1 ? "" : "s"}
                </div>
              </div>
              {detail.locked ? (
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><Lock size={12} className="mr-1" /> Super Admin — always full access</Badge>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={clearAll} disabled={busy || (grants.size === 0 && denies.size === 0)} data-testid="override-clear-btn">
                    <Broom size={14} className="mr-1" /> Clear overrides
                  </Button>
                  <Button size="sm" onClick={save} disabled={!dirty || busy} className="btn-primary-blue" data-testid="override-save-btn">
                    <FloppyDisk size={14} className="mr-1" /> {busy ? "Saving…" : "Save"}
                  </Button>
                </>
              )}
            </div>

            {!detail.locked && (
              <div className="border-b border-slate-100 px-4 py-2.5">
                <Input
                  value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason for this change (recorded in the change history)…"
                  className="h-8 text-sm" data-testid="override-reason-input"
                />
              </div>
            )}

            <div className="max-h-[58vh] overflow-y-auto p-4">
              {(catalog?.modules || []).map((mod) => (
                <div key={mod.key} className="mb-5" data-testid={`override-module-${mod.key}`}>
                  <div className="mb-1.5 font-mono-alt text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
                    {mod.label}
                  </div>
                  <div className="space-y-1">
                    {mod.actions.map((a) => {
                      const base = baseSet.has(a.key);
                      const st = stateOf(a.key);
                      const effective = st === "grant" || (st === "inherit" && base);
                      return (
                        <div key={a.key} className="flex items-center gap-3 rounded-lg border border-slate-100 px-3 py-1.5">
                          <span className="w-4">
                            {effective
                              ? <Check size={14} className="text-emerald-600" data-testid={`override-effective-yes-${a.key}`} />
                              : <Minus size={14} className="text-slate-300" data-testid={`override-effective-no-${a.key}`} />}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm text-slate-700">{a.label}</div>
                            <div className="font-mono-alt text-[9px] text-slate-300">{a.key}</div>
                          </div>
                          <StateToggle
                            state={st} base={base} disabled={detail.locked || busy}
                            onChange={(s) => setState(a.key, s)}
                            testid={`override-toggle-${a.key}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
